<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { RelatedFootageReason } from '@roadfootage/api-client'
import type { RelatedIncidentReportDto } from '@roadfootage/api-client'
import { formatIncidentDateTime, formatIncidentType } from '@/utils/format'

const props = defineProps<{
  items: RelatedIncidentReportDto[]
}>()

// An item without an id cannot be linked to.
const rows = computed(() => props.items.filter((i) => !!i.id))

const router = useRouter()

const reasonLabel = (reason: RelatedFootageReason | undefined) =>
  reason === RelatedFootageReason.SameRecordedCrash ? 'Same recorded crash' : 'Same incident'

const title = (item: RelatedIncidentReportDto) =>
  item.suburb
    ? `${formatIncidentType(item.incidentReportType)} · ${item.suburb}`
    : formatIncidentType(item.incidentReportType)

function open(id: string) {
  void router.push({ name: 'preview', params: { incidentReportId: id } })
}
</script>

<template>
  <q-card v-if="rows.length > 0" data-testid="related-footage">
    <q-card-section class="q-pb-none">
      <div class="text-subtitle1 text-weight-medium">Related footage ({{ rows.length }})</div>
    </q-card-section>

    <q-list separator>
      <q-item
        v-for="item in rows"
        :key="item.id"
        clickable
        tag="a"
        :href="`/preview/${item.id}`"
        data-testid="related-item"
        @click.prevent="open(item.id as string)"
      >
        <q-item-section avatar>
          <q-img
            v-if="item.thumbnailUrl"
            :src="item.thumbnailUrl"
            :ratio="16 / 9"
            width="96px"
            class="rounded-borders"
            alt=""
          />
          <div v-else class="related-thumb-placeholder flex flex-center rounded-borders bg-grey-3">
            <q-icon name="mdi-video-outline" color="grey-7" size="sm" />
          </div>
        </q-item-section>
        <q-item-section>
          <q-item-label>{{ title(item) }}</q-item-label>
          <q-item-label caption>
            {{ formatIncidentDateTime(item.incidentDateTime, item.incidentTimezone) }}
          </q-item-label>
          <q-item-label caption>{{ reasonLabel(item.reason) }}</q-item-label>
        </q-item-section>
        <q-item-section side>
          <q-icon name="mdi-chevron-right" />
        </q-item-section>
      </q-item>
    </q-list>
  </q-card>
</template>

<style scoped>
.related-thumb-placeholder {
  width: 96px;
  height: 54px;
}
</style>
